import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { usePetsStore } from '../store'
import { useSuccessToast } from '../../../shared/components/Toast'
import { Button } from '../../../shared/components/Button'
import { Input, Textarea, Select, FormRow } from '../../../shared/components/Input'
import { PageLoader } from '../../../shared/components/Loading'
import { ImageUpload } from '../../../shared/components/ImageUpload'
import { ArrowLeft, Save, Dog, Cat, PawPrint } from 'lucide-react'
import { createPetSchema } from '@rondela/shared'
import type { PetCreate } from '@rondela/shared'

interface FormErrors {
  nombre?: string
  especie?: string
  raza?: string
  fechaNacimiento?: string
  peso?: string
  condicionesMedicas?: string
  notas?: string
}

export function PetFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const showSuccess = useSuccessToast()
  const { currentPet, isLoading, error, fetchPet, createPet, updatePet, deletePhoto, clearCurrentPet, clearError } = usePetsStore()
  const isEditing = !!id

  const [formData, setFormData] = useState<PetCreate>({
    nombre: '',
    especie: 'perro',
    raza: '',
    fechaNacimiento: '',
    peso: undefined,
    condicionesMedicas: '',
    notas: '',
  })

  const [errors, setErrors] = useState<FormErrors>({})

  useEffect(() => {
    if (id) {
      fetchPet(id)
    }
    return () => {
      clearCurrentPet()
      clearError()
    }
  }, [id, fetchPet, clearCurrentPet, clearError])

  useEffect(() => {
    if (currentPet && isEditing) {
      setFormData({
        nombre: currentPet.nombre,
        especie: currentPet.especie,
        raza: currentPet.raza || '',
        fechaNacimiento: currentPet.fechaNacimiento?.split('T')[0] || '',
        peso: currentPet.peso || undefined,
        condicionesMedicas: currentPet.condicionesMedicas || '',
        notas: currentPet.notas || '',
      })
    }
  }, [currentPet, isEditing])

  const validateForm = (): boolean => {
    const result = createPetSchema.safeParse(formData)
    if (!result.success) {
      const fieldErrors: FormErrors = {}
      result.error.errors.forEach((err) => {
        const field = err.path[0] as keyof FormErrors
        fieldErrors[field] = err.message
      })
      setErrors(fieldErrors)
      return false
    }
    setErrors({})
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      if (isEditing && id) {
        await updatePet(id, formData)
        showSuccess('Mascota actualizada correctamente')
        navigate(`/mascotas/${id}`)
      } else {
        const pet = await createPet(formData)
        showSuccess('Mascota creada correctamente')
        navigate(`/mascotas/${pet.id}`)
      }
    } catch {
      // Error already handled in store
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value ? Number(value) : undefined) : value,
    }))
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  if (isEditing && isLoading && !currentPet) {
    return <PageLoader />
  }

  const speciesOptions = [
    { value: 'perro', label: 'Perro' },
    { value: 'gato', label: 'Gato' },
    { value: 'otro', label: 'Otro' },
  ]

  const getSpeciesIcon = () => {
    switch (formData.especie) {
      case 'perro':
        return <Dog size={32} />
      case 'gato':
        return <Cat size={32} />
      default:
        return <PawPrint size={32} />
    }
  }

  const handlePhotoUpload = (_url: string) => {
    // Photo is already updated in the store by the upload endpoint
    showSuccess('Foto actualizada correctamente')
  }

  const handlePhotoRemove = async () => {
    if (id) {
      try {
        await deletePhoto(id)
        showSuccess('Foto eliminada correctamente')
      } catch {
        // Error handled in store
      }
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <Link to={isEditing ? `/mascotas/${id}` : '/mascotas'} className="btn btn-ghost">
            <ArrowLeft size={16} />
            Volver
          </Link>
          <h1>{isEditing ? 'Editar mascota' : 'Nueva mascota'}</h1>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={clearError} className="alert-dismiss">&times;</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="form">
        {isEditing && id && (
          <div className="form-field">
            <label>Foto de perfil</label>
            <ImageUpload
              currentImageUrl={currentPet?.fotoUrl}
              onUpload={handlePhotoUpload}
              onRemove={currentPet?.fotoUrl ? handlePhotoRemove : undefined}
              uploadEndpoint={`/api/uploads/pets/${id}/photo`}
              fieldName="photo"
              size="lg"
              placeholder={
                <>
                  {getSpeciesIcon()}
                  <span>Subir foto</span>
                </>
              }
            />
          </div>
        )}

        <Input
          label="Nombre"
          name="nombre"
          value={formData.nombre}
          onChange={handleChange}
          error={errors.nombre}
          required
          autoFocus={!isEditing}
        />

        <Select
          label="Especie"
          name="especie"
          value={formData.especie}
          onChange={handleChange}
          options={speciesOptions}
          error={errors.especie}
        />

        <Input
          label="Raza"
          name="raza"
          value={formData.raza}
          onChange={handleChange}
          error={errors.raza}
          hint="Opcional"
        />

        <FormRow>
          <Input
            label="Fecha de nacimiento"
            name="fechaNacimiento"
            type="date"
            value={formData.fechaNacimiento}
            onChange={handleChange}
            error={errors.fechaNacimiento}
          />

          <Input
            label="Peso (kg)"
            name="peso"
            type="number"
            value={formData.peso || ''}
            onChange={handleChange}
            error={errors.peso}
            step="0.1"
            min="0"
          />
        </FormRow>

        <Textarea
          label="Condiciones médicas"
          name="condicionesMedicas"
          value={formData.condicionesMedicas}
          onChange={handleChange}
          error={errors.condicionesMedicas}
          rows={3}
          hint="Alergias, condiciones crónicas, etc."
        />

        <Textarea
          label="Notas"
          name="notas"
          value={formData.notas}
          onChange={handleChange}
          error={errors.notas}
          rows={3}
        />

        <div className="form-actions">
          <Link to={isEditing ? `/mascotas/${id}` : '/mascotas'} className="btn btn-secondary">
            Cancelar
          </Link>
          <Button type="submit" variant="primary" loading={isLoading} icon={Save}>
            {isEditing ? 'Guardar cambios' : 'Crear mascota'}
          </Button>
        </div>
      </form>
    </div>
  )
}
